import { useTandemTradingBlocked } from '../../../../features/tandem/TandemSessionBoundary';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Radio, InputNumber, Input, message } from 'antd';

import {
  selectors,
  createOrder,
  SHORT_SELL,
  BUY,
  SELL,
  CASH_APPROACH,
  REAL_TIME_SCENARIO,
} from 'state';
import { formatters } from 'services';
import {
  OverLimitIcon,
  CannotSellIcon,
  CannotShortSellIcon,
  CannotBuyIcon,
  CannotSellPopover,
  CannotShortPopover,
  CannotSellBecauseHTMisLocked,
  CannotShortSellIcon2,
  CannotCoverMargin,
} from './InfoPanels';
import useMaxQuantity from './useMaxQuantity';
import usePrices from './usePrices';
import useCollateralSign from './useCollateral';
import {
  notionalCapacityStatus,
  resolveNotionalMaximum,
  useNotionalCapacity,
} from '../../useNotionalCapacity';
import { useReportingCurrency } from '../../../../state/app/utils';

const { TextArea } = Input;

const TicketMetric: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="markets-v4-ticket-metric">
    <span>{label}</span>
    <strong>{children}</strong>
  </div>
);

type Props = {
  id: number;
  onComplete: () => void;
  tradingBlocked: boolean;
};

const UnavailableOrder: React.FC<{ reason: string }> = ({ reason }) => (
  <div className="markets-v3-form markets-v4-ticket markets-v4-order-ticket is-unavailable">
    <div className="markets-v3-form__body markets-v4-ticket__body">
      <section className="markets-v4-ticket-section">
        <div className="markets-v4-ticket-section__head">
          <span>Order setup</span>
          <small>Unavailable</small>
        </div>
        <div className="markets-v4-ticket-unavailable">
          <strong>Order unavailable</strong>
          <span>{reason}</span>
        </div>
      </section>
    </div>
    <div className="markets-v3-form__footer markets-v4-ticket__footer">
      <Button type="primary" block disabled>
        Place market order
      </Button>
    </div>
  </div>
);

const OrdinaryOrderForm: React.FC<Props> = ({
  id,
  onComplete,
  tradingBlocked,
}) => {
  const tandemBlocked = useTandemTradingBlocked();
  const dispatch = useDispatch();

  const session = useSelector(selectors.activeSession);
  const {
    transaction_fee,
    risk_approach,
    allow_short_sell,
    allow_short_only_on_fx,
    starting_cash_amount,
    short_sell_leverage,
    bond_leverage,
    equity_leverage,
    etf_equities_leverage,
    etf_commodities_leverage,
    futures_leverage,
  } = session;
  const player = useSelector(selectors.player);
  const instruments = useSelector(selectors.financialInstruments);
  const instrument = useSelector(selectors.financialInstrumentById(id));
  const asset = useSelector(selectors.underlyingAssetOfInstrument(instrument));
  const isHtm = asset.resourcetype === 'HTMBond';
  const fxShortSell = allow_short_only_on_fx && asset.resourcetype === 'FX';
  const allowShortSell = (allow_short_sell && !isHtm) || fxShortSell;
  const currency = useSelector(selectors.currencyById(asset.base_currency));
  const reportingCurrency = useReportingCurrency(session.id);
  const [quantity, setQuantity] = useState(100);
  const [transaction, setTransaction] = useState(BUY);

  const { price, reportingPrice } = usePrices(id, transaction);
  // console.log(price, reportingPrice);
  const [investmentRationale, setInvestmentRationale] = useState<string>();

  useEffect(() => {
    //pass
  }, [instruments, dispatch]);

  // console.log(session.require_investment_rationale);
  // console.log(investmentRationale);
  let satisfiesInvestmentRationaleRequirement;
  let investmentRationalePlaceholder;
  if (session.require_investment_rationale === false) {
    satisfiesInvestmentRationaleRequirement = true;
    investmentRationalePlaceholder = 'Investment Rationale';
  } else {
    investmentRationalePlaceholder =
      'Investment Rationale | required field min. 10 char.';
    if (
      investmentRationale !== undefined &&
      investmentRationale !== '' &&
      investmentRationale.length > 9
    ) {
      satisfiesInvestmentRationaleRequirement = true;
    } else {
      satisfiesInvestmentRationaleRequirement = false;
    }
  }
  // console.log(satisfiesInvestmentRationaleRequirement);
  const isFX = asset.resourcetype === 'FX';
  const isBond =
    asset.resourcetype == 'GovtBond' ||
    asset.resourcetype == 'CorporateBond' ||
    asset.resourcetype == 'HTMBond' ||
    asset.resourcetype == 'AFSBond';
  const isEquity = asset.resourcetype == 'Equity';
  const isETFEquities = asset.resourcetype == 'ETFEquities';
  const isETFCommodities = asset.resourcetype == 'ETFCommodities';
  const isFutures = asset.resourcetype == 'Futures';
  const isBondAndSell = isBond && transaction === SELL;
  const isBondAndRealTime =
    isBond && session.scenario_type === REAL_TIME_SCENARIO;
  const isLeveragedBond =
    isBond && bond_leverage != 1 && risk_approach === CASH_APPROACH;
  const isLeveragedEquity =
    isEquity && equity_leverage != 1 && risk_approach === CASH_APPROACH;
  const isLeveragedETFEquities =
    isETFEquities &&
    etf_equities_leverage != 1 &&
    risk_approach === CASH_APPROACH;
  const isLeveragedETFCommodities =
    isETFCommodities &&
    etf_commodities_leverage != 1 &&
    risk_approach === CASH_APPROACH;
  const isLeveragedFutures =
    isFutures && futures_leverage != 1 && risk_approach === CASH_APPROACH;

  const isLeveraged =
    isLeveragedEquity ||
    isLeveragedETFEquities ||
    isLeveragedETFCommodities ||
    isLeveragedBond ||
    isLeveragedFutures;
  let leverage = 1;
  if (isLeveragedBond) {
    leverage = bond_leverage;
  }
  if (isLeveragedEquity) {
    leverage = equity_leverage;
  }
  if (isLeveragedETFEquities) {
    leverage = etf_equities_leverage;
  }
  if (isLeveragedETFCommodities) {
    leverage = etf_commodities_leverage;
  }
  if (isLeveragedFutures) {
    leverage = futures_leverage;
  }
  const { maxQuantityHardLimits, maxQuantitySoftLimits } = useMaxQuantity(
    id,
    transaction,
  );
  let maxQuantity = session.hard_limits
    ? maxQuantityHardLimits
    : maxQuantitySoftLimits;
  if (isFX) {
    maxQuantity = maxQuantityHardLimits;
  }
  const notionalCapacity = useNotionalCapacity(id, transaction);
  maxQuantity = resolveNotionalMaximum({
    localMaximum: maxQuantity,
    capacity: notionalCapacity,
    transaction,
    signedPosition: instrument.amount,
  });
  const capacityStatus = notionalCapacityStatus(notionalCapacity);
  const v2CapacityApplied =
    notionalCapacity.usesBackend && notionalCapacity.result?.applies !== false;
  // console.log(maxQuantity);
  // 14168

  // Futures: the engine trades in units, but the player thinks in CONTRACTS (1 contract = lot_size units).
  // `quantity` stays in units (source of truth); the input below is a contracts view over it, and the
  // submit path is unchanged. lotSize defaults to 1 (=> contracts == units) so nothing changes for
  // fully-unset/legacy futures, and this block is futures-gated so other asset classes are untouched.
  const lotSize = isFutures ? asset.lot_size || 1 : 1;
  const maxContracts = Math.floor(maxQuantity / lotSize);
  const quantityTooLarge = quantity > maxQuantity;
  const quantityInvalid = quantity <= 0 || quantityTooLarge;
  const quantityInputStatus = quantityTooLarge ? 'error' : undefined;
  const quantityInputStyles = quantityTooLarge
    ? { input: { color: '#d54848' } }
    : undefined;

  // Start a futures order at 1 contract (default quantity is 100 units, which may not be a clean
  // multiple of lot_size). Keyed on asset.id so it only re-initializes when the modal targets a new asset.
  useEffect(() => {
    if (isFutures) {
      setQuantity(lotSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const collateralSign = useCollateralSign(id, transaction);

  if (!instrument) {
    message.error('Could not retrieve financial instrument!');
    return null;
  }
  if (!price) {
    return (
      <UnavailableOrder reason="This instrument is visible, but it cannot be traded until a usable quote is available." />
    );
  }

  const wallet = player.wallets.find(
    wallet => asset.variable_currency === wallet.currency,
  );
  if (!wallet) {
    message.error('Could not retrieve wallet!');
    return (
      <UnavailableOrder reason="A compatible cash wallet is required before this instrument can be traded." />
    );
  }
  const displayBondPrice = formatters.commas2Digits(
    price * 100 - asset.accrued_interest,
  );
  const displayCleanPrice = `${formatters.commas2Digits(
    price * 100 - asset.accrued_interest,
  )} %`;
  const displayAccruedInterest = `${formatters.commas2Digits(
    asset.accrued_interest,
  )} %`;
  const value = quantity * price;
  const total = value * (1 + transaction_fee);
  let collateral;
  if (collateralSign === -1) {
    collateral =
      (Math.abs(quantity) / Math.abs(instrument.amount)) *
      instrument.collateral *
      -1;
  } else {
    collateral = collateralSign * total * leverage;
  }
  const cantCoverCollateral = isLeveraged && collateral > wallet.amount;

  // INFO PANEL LOGIC
  const assetLimitPercentage = isFX ? session.fx_leverage : instrument.pv_limit;
  const assetLimit = assetLimitPercentage * starting_cash_amount;
  const showOverLimitIcon =
    !v2CapacityApplied &&
    transaction === BUY &&
    reportingPrice * (instrument.amount + 1) >= assetLimit &&
    !isLeveraged;
  const showCannotSellIcon = transaction === SELL && instrument.amount === 0;
  const showCannotShortIcon2 =
    !v2CapacityApplied &&
    transaction === SHORT_SELL &&
    maxQuantity === 0 &&
    !isLeveraged;
  const showCannotShortIcon =
    player.remaining_short_sell_amount_limit < 1000 &&
    transaction === SHORT_SELL;
  const cannotBuy =
    transaction === BUY &&
    wallet.amount < price &&
    asset.resourcetype !== 'FX' &&
    !isLeveraged;

  const disableHTMSellButton = isHtm && !session.allow_htm;
  const disableSellButton = instrument.amount < 0;
  const disableShortButton = instrument.amount > 0;

  // const isFX = asset.resourcetype == 'FX';
  // const notFXnotBond = (isBond && isFX);

  const isNotBondOrFXnonGBP =
    asset.resourcetype !== 'GovtBond' &&
    asset.resourcetype !== 'CorporateBond' &&
    asset.resourcetype !== 'HTMBond' &&
    asset.resourcetype !== 'AFSBond' &&
    asset.resourcetype !== 'FX' &&
    currency.symbol !== 'GBP';

  const isNotBondOrFXGBP =
    asset.resourcetype !== 'GovtBond' &&
    asset.resourcetype !== 'CorporateBond' &&
    asset.resourcetype !== 'HTMBond' &&
    asset.resourcetype !== 'AFSBond' &&
    asset.resourcetype !== 'FX' &&
    currency.symbol === 'GBP';

  const displayedPrice = isBond
    ? `${displayBondPrice} %`
    : isFX
      ? formatters.commas4Digits(price)
      : isNotBondOrFXnonGBP
        ? formatters.commas2Digits(price)
        : formatters.commasExact4Digits(price);
  const summaryPrice = isBond
    ? `${formatters.commas2Digits(price * 100)} %`
    : displayedPrice;
  const hasOrderWarning =
    showOverLimitIcon ||
    showCannotSellIcon ||
    showCannotShortIcon ||
    showCannotShortIcon2 ||
    cantCoverCollateral ||
    cannotBuy;

  return (
    <div className="markets-v3-form markets-v4-ticket markets-v4-order-ticket">
      <div className="markets-v3-form__body markets-v4-ticket__body">
        <section className="markets-v4-ticket-section">
          <div className="markets-v4-ticket-section__head">
            <span>Order setup</span>
            <small>Market order</small>
          </div>
          <div className="markets-v4-ticket-field markets-v4-ticket-field--direction">
            <label>Transaction</label>
            <Radio.Group
              onChange={e => setTransaction(e.target.value)}
              value={transaction}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={BUY}>Buy</Radio.Button>

              {isHtm ? (
                <CannotSellBecauseHTMisLocked visible={disableHTMSellButton}>
                  <Radio.Button value={SELL} disabled={disableHTMSellButton}>
                    Sell
                  </Radio.Button>
                </CannotSellBecauseHTMisLocked>
              ) : (
                <CannotSellPopover visible={disableSellButton}>
                  <Radio.Button value={SELL} disabled={disableSellButton}>
                    Sell
                  </Radio.Button>
                </CannotSellPopover>
              )}

              {allowShortSell && (
                <CannotShortPopover visible={disableShortButton}>
                  <Radio.Button
                    value={SHORT_SELL}
                    disabled={disableShortButton}
                  >
                    Short Sell
                  </Radio.Button>
                </CannotShortPopover>
              )}
            </Radio.Group>
          </div>

          <div className="markets-v4-ticket-setup-grid">
            <div className="markets-v4-ticket-field markets-v4-ticket-field--quantity">
              <label>{isFutures ? 'Contracts' : 'Quantity'}</label>
              <div className="markets-v4-ticket-input-line">
                {isFutures ? (
                  <InputNumber
                    aria-label="Contracts"
                    value={Math.floor(quantity / lotSize)}
                    step={1}
                    formatter={formatters.commasNoDigits}
                    onChange={val => setQuantity((Number(val) || 0) * lotSize)}
                    status={quantityInputStatus}
                    styles={quantityInputStyles}
                  />
                ) : (
                  <InputNumber
                    aria-label="Quantity"
                    value={quantity}
                    formatter={formatters.commasNoDigits}
                    onChange={val => setQuantity(Number(val || 0))}
                    status={quantityInputStatus}
                    styles={quantityInputStyles}
                  />
                )}
                <span>
                  Max{' '}
                  {formatters.commasNoDigits(
                    isFutures ? maxContracts : maxQuantity,
                  )}
                </span>
              </div>
              {quantityTooLarge && (
                <small role="alert" style={{ color: '#d54848' }}>
                  Maximum allowed is{' '}
                  {formatters.commasNoDigits(
                    isFutures ? maxContracts : maxQuantity,
                  )}
                  .
                </small>
              )}
              {isFutures && (
                <small>
                  1 contract = {formatters.commasNoDigits(lotSize)}
                  {asset.lot_unit ? ` ${asset.lot_unit}` : ''}
                </small>
              )}
              {isBondAndSell && (
                <small>
                  Owned {formatters.commasNoDigits(instrument.amount)} · Repoed{' '}
                  {formatters.commasNoDigits(instrument.repoed_amount)}
                </small>
              )}
            </div>

            <TicketMetric label={`Price (${currency.symbol})`}>
              {displayedPrice}
            </TicketMetric>
            {v2CapacityApplied ? (
              <TicketMetric
                label={`Notional limit · ${reportingCurrency.symbol}`}
              >
                {notionalCapacity.result?.configured_limit_reporting == null
                  ? '—'
                  : formatters.commas2Digits(
                      Number(
                        notionalCapacity.result.configured_limit_reporting,
                      ),
                    )}
              </TicketMetric>
            ) : (
              <TicketMetric label={`Limit · AUM ${reportingCurrency.symbol}`}>
                {formatters.percent0Digits(assetLimitPercentage)}
              </TicketMetric>
            )}
          </div>

          {v2CapacityApplied && capacityStatus ? (
            <small>{capacityStatus}</small>
          ) : null}

          {hasOrderWarning && (
            <div
              className="markets-v4-ticket-warnings"
              aria-label="Order warnings"
            >
              <span>Check order</span>
              {showOverLimitIcon && (
                <OverLimitIcon
                  financialInstrument={instrument}
                  limit={assetLimitPercentage}
                />
              )}
              {showCannotSellIcon && (
                <CannotSellIcon allowShort={allow_short_sell} />
              )}
              {showCannotShortIcon && (
                <CannotShortSellIcon shortSellLimit={short_sell_leverage} />
              )}
              {showCannotShortIcon2 && (
                <CannotShortSellIcon2 pv_limit={instrument.pv_limit} />
              )}
              {cantCoverCollateral && <CannotCoverMargin />}
              {cannotBuy && <CannotBuyIcon symbol={currency.symbol} />}
            </div>
          )}
        </section>

        {isBondAndRealTime && (
          <section className="markets-v4-ticket-section">
            <div className="markets-v4-ticket-section__head">
              <span>Bond pricing</span>
            </div>
            <div className="markets-v4-ticket-metrics markets-v4-ticket-metrics--four">
              <TicketMetric label="Clean price">
                {displayCleanPrice}
              </TicketMetric>
              <TicketMetric label="Dirty price">
                {formatters.commas2Digits(price * 100)} %
              </TicketMetric>
              <TicketMetric label="Accrued interest">
                {displayAccruedInterest}
              </TicketMetric>
              <TicketMetric label="Dirty amount">
                {formatters.commasNoDigits(price * quantity)} {currency.symbol}
              </TicketMetric>
            </div>
          </section>
        )}

        <section className="markets-v4-ticket-section">
          <div className="markets-v4-ticket-section__head">
            <span>Order summary</span>
          </div>
          <div className="markets-v4-ticket-metrics markets-v4-ticket-metrics--summary">
            <TicketMetric label={isFutures ? 'Contracts' : 'Quantity'}>
              {isFutures
                ? formatters.commasNoDigits(
                    formatters.unitsToContracts(quantity, lotSize),
                  )
                : formatters.commasNoDigits(quantity)}
            </TicketMetric>
            <TicketMetric label="Price">{summaryPrice}</TicketMetric>
            <TicketMetric label="Value">
              {formatters.commasNoDigits(value)}
            </TicketMetric>
            <TicketMetric label="Fees*">
              {formatters.percent1Digit(transaction_fee)}
            </TicketMetric>
            <TicketMetric label="Total">
              {formatters.commasNoDigits(total)}
            </TicketMetric>
          </div>
          <div className="markets-v4-ticket-summary-foot">
            {risk_approach === CASH_APPROACH && (
              <span>
                Available cash{' '}
                <strong>{formatters.commasNoDigits(wallet.amount)}</strong>
              </span>
            )}
            {isLeveraged && (
              <span className={cantCoverCollateral ? 'is-negative' : ''}>
                Margin <strong>{formatters.commasNoDigits(collateral)}</strong>
              </span>
            )}
          </div>
        </section>

        <section className="markets-v4-ticket-section markets-v4-ticket-section--context">
          <div className="markets-v4-ticket-field markets-v4-ticket-field--rationale">
            <label>Investment rationale</label>
            <TextArea
              rows={2}
              maxLength={511}
              minLength={10}
              placeholder={investmentRationalePlaceholder}
              value={investmentRationale}
              onChange={e => setInvestmentRationale(e.target.value)}
            />
          </div>
        </section>
      </div>
      <div className="markets-v3-form__footer markets-v4-ticket__footer">
        <Button
          type="primary"
          block
          disabled={
            tandemBlocked || tradingBlocked ||
            quantityInvalid ||
            cantCoverCollateral ||
            !satisfiesInvestmentRationaleRequirement
          }
          onClick={() => {
            if (quantityInvalid) return;
            dispatch(
              createOrder({
                resourcetype: 'MarketOrder',
                financial_instrument: instrument.id,
                forecast: 0,
                transaction,
                quantity,
                limit_price: 0,
                investment_rationale: investmentRationale,
                hedge: false,
              }),
            );
            onComplete();
          }}
        >
          Place market order
        </Button>
      </div>
    </div>
  );
};

export default OrdinaryOrderForm;
